import React from 'react';
import { 
  CategoryData, 
  EmissionCategory, 
  CATEGORY_DISPLAY_NAMES, 
  getCategoryBgColor, 
  getCategoryTextColor,
  formatToMTCO2e,
  formatNumberWithCommas,
  getReportingPercentageColor
} from '../../lib/emission-categories';

interface EmissionCategoryCardsProps {
  categoryData: Record<EmissionCategory, CategoryData>;
  baseYearData?: Record<EmissionCategory, CategoryData>;
  selectedYear?: number;
  baseYear?: number;
}

export function EmissionCategoryCards({ 
  categoryData, 
  baseYearData,
  selectedYear = new Date().getFullYear(),
  baseYear
}: EmissionCategoryCardsProps) {
  const categories: EmissionCategory[] = ['energy', 'transportation', 'waste'];

  const getProgressBarColor = (category: EmissionCategory) => {
    switch (category) {
      case 'energy': return 'bg-green-500';
      case 'transportation': return 'bg-green-600';
      case 'waste': return 'bg-green-700';
      default: return 'bg-green-500';
    }
  };

  const getProgressBarBgColor = (category: EmissionCategory) => {
    switch (category) {
      case 'energy': return 'bg-green-200';
      case 'transportation': return 'bg-green-300';
      case 'waste': return 'bg-green-400';
      default: return 'bg-green-200';
    }
  };

  const getCategoryIcon = (category: EmissionCategory) => {
    switch (category) {
      case 'energy':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
          </svg>
        );
      case 'transportation':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8 16.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM15 16.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1V8a1 1 0 00-1-1h-3z" />
          </svg>
        );
      case 'waste':
        return (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      {categories.map((category) => {
        const data = categoryData[category];
        const baseData = baseYearData?.[category];
        const target = baseData?.totalEmissions || data.totalEmissions * 1.1; // Default to 10% higher if no base data
        const progressPercentage = target > 0 ? Math.min((data.totalEmissions / target) * 100, 100) : 0;

        return (
          <div 
            key={category}
            className={`${getCategoryBgColor(category)} rounded-xl p-6 border border-green-200 hover:shadow-lg transition-shadow`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-white/50 rounded-lg flex items-center justify-center mr-3">
                  {getCategoryIcon(category)}
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${getCategoryTextColor(category)}`}>
                    {CATEGORY_DISPLAY_NAMES[category]}
                  </h3>
                </div>
              </div>
            </div>

            {/* Emission Value */}
            <div className="mb-4">
              <div className={`text-3xl font-bold ${getCategoryTextColor(category)} mb-1`}>
                {formatNumberWithCommas(parseFloat(formatToMTCO2e(data.totalEmissions)))}
              </div>
              <div className="text-green-600 text-sm font-medium">
                MTCO2e
              </div>
            </div>

            {/* Reporting Status */}
            <div className="mb-4">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-medium ${getReportingPercentageColor(data.reportingPercentage)}`}>
                  {data.reportingPercentage}% REPORTING
                </span>
                <span className="text-green-600 text-xs">
                  {data.measuredCount}/{data.totalCount} entries
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-700 font-medium">Progress</span>
                <span className="text-sm text-green-600">
                  {formatNumberWithCommas(parseFloat(formatToMTCO2e(data.totalEmissions)))} / {formatNumberWithCommas(parseFloat(formatToMTCO2e(target)))}
                </span>
              </div>
              <div className={`w-full h-3 ${getProgressBarBgColor(category)} rounded-full overflow-hidden`}>
                <div 
                  className={`h-full ${getProgressBarColor(category)} transition-all duration-500 ease-out`}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-green-600">
                <span>Target: {formatNumberWithCommas(parseFloat(formatToMTCO2e(target)))} MTCO2e</span>
                <span>{Math.round(progressPercentage)}%</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}













